import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Clock,
  UserPlus,
  Calendar,
  Check,
  X,
  RefreshCw,
  Lightbulb,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface CalendarSuggestion {
  id: number;
  calendarAccountId: number;
  syncedEventId: number | null;
  suggestionType: string;
  title: string;
  description: string | null;
  suggestedStart: string | null;
  suggestedEnd: string | null;
  relatedClientId: number | null;
  relatedProspectId: number | null;
  confidence: string | null;
  reasoning: string | null;
  status: string;
  createdAt: string;
}

interface CalendarAiInsightsProps {
  calendarAccountId?: number;
}

export function CalendarAiInsights({ calendarAccountId }: CalendarAiInsightsProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [analyzeCooldown, setAnalyzeCooldown] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<CalendarSuggestion | null>(null);
  const [overrides, setOverrides] = useState<{
    title?: string;
    startTime?: string;
    endTime?: string;
  }>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch pending suggestions
  const { data: suggestionsData, isLoading } = useQuery<{
    suggestions: CalendarSuggestion[];
  }>({
    queryKey: ["/api/calendar-suggestions", calendarAccountId],
    queryFn: async () => {
      const params = new URLSearchParams({ status: "pending" });
      if (calendarAccountId) {
        const response = await fetch(
          `/api/calendar-accounts/${calendarAccountId}/suggestions?${params}`
        );
        if (!response.ok) throw new Error("Failed to fetch suggestions");
        return response.json();
      }
      const response = await fetch(`/api/calendar-suggestions?${params}`);
      if (!response.ok) throw new Error("Failed to fetch suggestions");
      return response.json();
    },
    enabled: true,
  });

  const suggestions = suggestionsData?.suggestions || [];

  // Analyze conflicts mutation
  const analyzeConflictsMutation = useMutation({
    mutationFn: async () => {
      if (!calendarAccountId) throw new Error("No calendar account selected");
      const response = await fetch(
        `/api/calendar-accounts/${calendarAccountId}/analyze-conflicts`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Analysis failed");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-suggestions"] });
      toast({
        title: "Analysis Complete",
        description: "Conflict analysis has been completed",
      });
      setAnalyzeCooldown(true);
      setTimeout(() => setAnalyzeCooldown(false), 30_000);
    },
    onError: (error: Error) => {
      toast({
        title: "Analysis Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Analyze follow-ups mutation
  const analyzeFollowUpsMutation = useMutation({
    mutationFn: async () => {
      if (!calendarAccountId) throw new Error("No calendar account selected");
      const response = await fetch(
        `/api/calendar-accounts/${calendarAccountId}/analyze-follow-ups`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Analysis failed");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-suggestions"] });
      toast({
        title: "Analysis Complete",
        description: "Follow-up analysis has been completed",
      });
      setAnalyzeCooldown(true);
      setTimeout(() => setAnalyzeCooldown(false), 30_000);
    },
    onError: (error: Error) => {
      toast({
        title: "Analysis Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Accept suggestion mutation
  const acceptMutation = useMutation({
    mutationFn: async ({
      id,
      overrides,
    }: {
      id: number;
      overrides?: { title?: string; startTime?: string; endTime?: string };
    }) => {
      const response = await fetch(`/api/calendar-suggestions/${id}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(overrides),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to accept suggestion");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });
      setAcceptDialogOpen(false);
      setSelectedSuggestion(null);
      setOverrides({});
      toast({
        title: "Suggestion Accepted",
        description: "Appointment has been created",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Reject suggestion mutation
  const rejectMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/calendar-suggestions/${id}/reject`, {
        method: "POST",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to reject suggestion");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-suggestions"] });
      toast({
        title: "Suggestion Rejected",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Dismiss suggestion mutation
  const dismissMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/calendar-suggestions/${id}/dismiss`, {
        method: "POST",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to dismiss suggestion");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-suggestions"] });
    },
  });

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case "conflict":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case "optimal_time":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "follow_up":
        return <UserPlus className="h-4 w-4 text-green-500" />;
      case "reschedule":
        return <Calendar className="h-4 w-4 text-orange-500" />;
      default:
        return <Lightbulb className="h-4 w-4 text-purple-500" />;
    }
  };

  const getSuggestionBadgeColor = (type: string) => {
    switch (type) {
      case "conflict":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "optimal_time":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "follow_up":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "reschedule":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      default:
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
    }
  };

  const handleAccept = (suggestion: CalendarSuggestion) => {
    setSelectedSuggestion(suggestion);
    setOverrides({
      title: suggestion.title,
      startTime: suggestion.suggestedStart
        ? new Date(suggestion.suggestedStart).toISOString().slice(0, 16)
        : "",
      endTime: suggestion.suggestedEnd
        ? new Date(suggestion.suggestedEnd).toISOString().slice(0, 16)
        : "",
    });
    setAcceptDialogOpen(true);
  };

  const handleConfirmAccept = () => {
    if (!selectedSuggestion) return;
    acceptMutation.mutate({
      id: selectedSuggestion.id,
      overrides: {
        title: overrides.title,
        startTime: overrides.startTime,
        endTime: overrides.endTime,
      },
    });
  };

  const isAnalyzing =
    analyzeConflictsMutation.isPending || analyzeFollowUpsMutation.isPending;
  const analyzeDisabled = isAnalyzing || analyzeCooldown || !calendarAccountId;

  return (
    <>
      <Card className="mb-6">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="py-3">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  AI Insights
                  {suggestions.length > 0 && (
                    <Badge variant="secondary">{suggestions.length}</Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {calendarAccountId && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={analyzeDisabled}
                          title={analyzeCooldown ? "Analysis recently completed" : undefined}
                        >
                          <RefreshCw
                            className={`h-4 w-4 mr-2 ${isAnalyzing ? "animate-spin" : ""}`}
                          />
                          Analyze
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            analyzeConflictsMutation.mutate();
                          }}
                        >
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          Find Conflicts
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            analyzeFollowUpsMutation.mutate();
                          }}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Suggest Follow-ups
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : suggestions.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No AI suggestions at this time</p>
                  {calendarAccountId && (
                    <p className="text-xs mt-1">
                      Click "Analyze" to generate insights
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {suggestions.slice(0, 5).map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className="border rounded-lg p-3 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          {getSuggestionIcon(suggestion.suggestionType)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm truncate">
                                {suggestion.title}
                              </span>
                              <Badge
                                variant="outline"
                                className={`text-xs ${getSuggestionBadgeColor(suggestion.suggestionType)}`}
                              >
                                {suggestion.suggestionType.replace("_", " ")}
                              </Badge>
                            </div>
                            {suggestion.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {suggestion.description}
                              </p>
                            )}
                            {suggestion.suggestedStart && (
                              <p className="text-xs text-muted-foreground mt-1">
                                <Clock className="h-3 w-3 inline mr-1" />
                                {format(
                                  new Date(suggestion.suggestedStart),
                                  "PPp"
                                )}
                              </p>
                            )}
                            {suggestion.confidence && (
                              <p className="text-xs text-muted-foreground">
                                Confidence:{" "}
                                {(parseFloat(suggestion.confidence) * 100).toFixed(0)}%
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {(suggestion.suggestionType === "follow_up" || suggestion.suggestionType === "optimal_time") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => handleAccept(suggestion)}
                              disabled={acceptMutation.isPending}
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => dismissMutation.mutate(suggestion.id)}
                            disabled={dismissMutation.isPending}
                          >
                            <X className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {suggestions.length > 5 && (
                    <p className="text-xs text-center text-muted-foreground">
                      +{suggestions.length - 5} more suggestions
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Accept Suggestion Dialog */}
      <Dialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Appointment</DialogTitle>
            <DialogDescription>
              Review and customize the appointment details before creating.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={overrides.title || ""}
                onChange={(e) =>
                  setOverrides({ ...overrides, title: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time</Label>
                <Input
                  id="startTime"
                  type="datetime-local"
                  value={overrides.startTime || ""}
                  onChange={(e) =>
                    setOverrides({ ...overrides, startTime: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime">End Time</Label>
                <Input
                  id="endTime"
                  type="datetime-local"
                  value={overrides.endTime || ""}
                  onChange={(e) =>
                    setOverrides({ ...overrides, endTime: e.target.value })
                  }
                />
              </div>
            </div>
            {selectedSuggestion?.reasoning && (
              <div className="rounded-md bg-muted p-3">
                <p className="text-xs text-muted-foreground">
                  <Lightbulb className="h-3 w-3 inline mr-1" />
                  {selectedSuggestion.reasoning}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAcceptDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAccept}
              disabled={acceptMutation.isPending}
            >
              {acceptMutation.isPending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Create Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
